import { getAlbumTracks, getArtistAlbums, getSeveralAlbums, getSeveralTracks } from "../../services/spotifyService";
import { chunkArray } from "./arrayOfObjects";

export function getAllArtistAlbums(artist: string): Promise<Record<string, any>[]> {
    return new Promise((resolve, reject) => {
        const albums: Record<string, any>[] = [];
        const getAlbums = async (offset = 0) => {
            const result = await getArtistAlbums(artist, offset);

            if (result==="error") {
                reject("error");
                return;
            }
            const data = result.data;
            albums.push(...data.items);
            // Spotify's `next` field is unreliable when include_groups has multiple
            // groups: pages truncate at group boundaries (e.g. album -> single) and
            // `next` comes back null before all items are returned. Paginate against
            // `total` instead, advancing by the number of items actually received.
            if (albums.length < data.total && data.items.length > 0) {
                getAlbums(offset + data.items.length);
            } else {
                resolve(albums);
            }
        };
        getAlbums();
    });
}

export function getAllTracks(album: Record<string, any>): Promise<Record<string, any>[]> {
    return new Promise((resolve, reject) => {
        // The several-albums endpoint already embeds each album's first 50
        // tracks, so only albums with more than 50 tracks need extra requests.
        const tracks:any = [...(album.tracks?.items ?? [])];
        const total = album.tracks?.total ?? tracks.length;
        const getTracks = async (offset: number) => {
            const result = await getAlbumTracks(album.id, offset);
            if (result==="error") {
                reject("error");
                return;
            }
            const data = result.data;
            tracks.push(...data.items);

            if (tracks.length < total && data.items.length > 0) {
                getTracks(offset + data.items.length);
            } else {
                resolve(tracks);
            }
        };
        if (tracks.length < total) {
            getTracks(tracks.length);
        } else {
            resolve(tracks);
        }
    });
}

export function getFullMetaDataAlbums(albums: Record<string, any>[]): Promise<Record<string, any>[]> {
    return new Promise((resolve, reject) => {
        const fullMetaDataAlbums: Record<string, any>[] = [];
        const getAlbums = async (offset = 0) => {
            const albumIds = albums.map(album => album.id).slice(offset, offset + 20).join(',');
            const result = await getSeveralAlbums(albumIds);
            if (result==="error") {
                reject("error");
                return;
            }
            const data = result.data;
            fullMetaDataAlbums.push(...data.albums);
            if (offset + 20 < albums.length) {
                getAlbums(offset + 20);
            } else {
                resolve(fullMetaDataAlbums);
            }
        };
        getAlbums();
    });
}

export async function getArtistTracks(artist: string): Promise<Record<string, any>[]> {
    const albums = await getAllArtistAlbums(artist);

    const fullMetaDataAlbums = await getFullMetaDataAlbums(albums);

    // Collect all track IDs from the albums, remembering which album each ID
    // was listed on — the /tracks lookup can re-link a track to a canonical
    // album outside the artist's catalogue (e.g. Drake's "For Free"), so the
    // album on the response can't be trusted for grouping
    const perAlbumTracks = await Promise.all(fullMetaDataAlbums.map(album => getAllTracks(album)));
    const trackIdToAlbum = new Map<string, Record<string, any>>();
    fullMetaDataAlbums.forEach((album, index) => {
        for (const track of perAlbumTracks[index]) {
            trackIdToAlbum.set(track.id, album);
        }
    });
    const allTrackIds = Array.from(trackIdToAlbum.keys());

    // Chunk the track IDs array into chunks of 50
    const trackIdChunks = chunkArray(allTrackIds, 50);

    // Fetch details for each chunk of track IDs
    const trackDetailsPromises = trackIdChunks.map(chunk => {
        const chunkedTrackIds = chunk.join(',');
        return getSeveralTracks(chunkedTrackIds);
    });

    const fetchedTrackDetails = await Promise.all(trackDetailsPromises);

    // A failed chunk means the track list would be silently incomplete —
    // fail the whole fetch instead so the user knows to retry
    if (fetchedTrackDetails.some(response => response === 'error')) {
        throw new Error('error');
    }

    // Extract and concatenate track objects from each response
    const tracks = fetchedTrackDetails
        .flatMap(response => response !== 'error' && response.data.tracks)
        .filter(track => track); // Filter out any potential undefined or null entries
    return mergeAlbumsAndTracks(trackIdToAlbum, tracks);
}

function mergeAlbumsAndTracks(trackIdToAlbum: Map<string, Record<string, any>>, tracks: Record<string, any>[]): Record<string, any>[] {
    return tracks
        .map((track) => {
            return { ...track, album: trackIdToAlbum.get(track.id) };
        })
        .filter((track) => track.album);
}
export function groupTracksByAlbum(tracks: Record<string, any>[]): Record<string, any>[] {
    const albums: Record<string, any>[] = [];

    tracks.forEach((track) => {
        const album = track.album;
        const albumIndex = albums.findIndex((a) => a.id === album.id);

        if (albumIndex === -1) {
            albums.push({ ...album, tracks: [track] });
        } else {
            albums[albumIndex].tracks.push(track);
        }
    });

    return albums.sort((a, b) => b.release_date.localeCompare(a.release_date));
}

export function getUniqueTrackNames(tracks: Record<string, any>[]): string[] {
    const trackNames: string[] = tracks.map((track) => track.name);

    return  Array.from(new Set<string>(trackNames).values());
}

